/* eslint react/no-danger: 0 */
/* eslint @atlaskit/ui-styling-standard/no-global-styles: 0 */
import type { FC } from 'react';
import React, { useEffect, useState } from 'react';

import type { ProgressBarContainer } from '@confluence/global-progress-bar-container/entry-points/ProgressBarContainer.ts';
import {
	DEFAULT_LOADING_BAR_COLOR,
	EXIT_ANIMATION_SPEED,
	NAV3_ID,
	NAV4_ID,
} from '@confluence/global-progress-bar-container/entry-points/defaultVariables.ts';

import { useProgressBarController } from './useProgressBarController.ts';
import { useProgressBarListenerController } from './useProgressBarListenerController.ts';

type WrappedProgressBarProps = {
	highlightColor?: string;
	status: string;
	progressBar: ProgressBarContainer;
	duration: number;
	isNav4: boolean;
};

type StyledAttributes = {
	transform?: string;
	transitionDuration?: string;
	isDelayed?: boolean;
	animation?: string;
	ssrProgressBarLoadPercent?: number;
	isNav4?: boolean;
};

const getStyledAttributes = (
	status,
	duration,
	ssrProgressBarDuration,
	ssrProgressBarLoadPercent,
) => {
	switch (status) {
		case 'START':
			return {
				transform: 'scaleX(0.99)',
				transitionDuration: `${duration}ms`,
			};

		case 'FINISH':
			return {
				transform: 'scaleX(1)',
				transitionDuration: `${EXIT_ANIMATION_SPEED}ms`,
			};

		case 'DELAYED':
			return {
				animation: 'shimmer 1200ms infinite',
				isDelayed: true,
				transform: 'scaleX(1)',
				transitionDuration: '0ms',
			};

		case 'SSR':
			// When browser is rendering the static HTML progress from 0 to 1/3
			// Then when SPA loads proceed from 33.33% to 99%, then 100% when apdex ends
			return {
				// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
				animation: process.env.REACT_SSR ? `ssr ${ssrProgressBarDuration}ms ease-out` : 'none',
				transform: `scaleX(${ssrProgressBarLoadPercent / 100})`,
				transitionDuration: '0ms',
			};

		case 'RESET':
		default:
			return {
				animation: 'none',
				transform: `scaleX(0)`,
				transitionDuration: '0ms',
			};
	}
};

//start Nav 4 styles
const generateLoadingBarStyles = ({
	animation = 'none',
	background,
	isDelayed,
	transform,
	isNav4,
	transitionDuration,
}: StyledAttributes & { background: string }): string => {
	return `
	body::before{
    content: '';
    display: block;
    animation: ${animation};
	  background: ${
			// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage
			background
		};
    height: 2px;
    z-index: 999;
    width: 100%;
    position: fixed;
    top: 0px;
    left: 0px;
	transform: ${transform};
	transition-duration: ${transitionDuration};
    transform-origin: 0 0;
    will-change: transform;
  }
  ${
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		process.env.REACT_SSR ? `@keyframes ssr { from { width: 0%; } to { width:100%; } }` : ''
	}
	${
		isDelayed
			? ` @keyframes shimmer { from { left: -15%; width: 15%; } to { left: 115%; width: 15%; } }`
			: ''
	}
	${
		isNav4
			? `opacity: '0.6',
	position: 'absolute',
	transformOrigin: '0 0',
	width: '100%',
	willChange: 'transform'`
			: ''
	}
	${
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		isNav4 && isDelayed && !process.env.REACT_SSR
			? `@keyframes shimmer {
			from: { left: '-15%', width: '15%' },
			to: { left: '115%', width: '15%' }
		}`
			: ''
	}
	${
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		isNav4 && !isDelayed && process.env.REACT_SSR
			? `@keyframes ssr {
			{
				from: { width: '0' },
				to: { width: '100%' },
			}
		}`
			: ''
	}
	${
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		isNav4 && isDelayed && process.env.REACT_SSR
			? `@keyframes ssr {
			from: { width: '0' },
			to: { width: '100%' },
		}
		@keyframes shimmer {
			from: { left: '-15%', width: '15%' },
			to: { left: '115%', width: '15%' },
		}`
			: ''
	}
`;
};
//end Nav 4 specific code

export const WrappedProgressBar: FC<WrappedProgressBarProps> = ({
	highlightColor = DEFAULT_LOADING_BAR_COLOR,
	status,
	duration,
	progressBar,
	isNav4,
}) => {
	const ssrAttributes = getStyledAttributes(
		'SSR',
		0,
		6000, // ssrProgressBarDuration
		80, // ssrProgressBarLoadPercent
	);

	// Initially, set progress bar to 0%, this ensures that progress bar does not start with 99% on the first load
	const initialAttributes = getStyledAttributes(
		'RESET',
		duration,
		6000, // ssrProgressBarDuration,
		80, // ssrProgressBarLoadPercent
	);

	const [attributes, setAttributes] = useState<StyledAttributes>(
		// If the page is currently rendering on the server-side or was rendered on server-side
		// We need to set the start point synchronously
		status === 'SSR' ? ssrAttributes : initialAttributes,
	);

	useProgressBarController(progressBar);
	useProgressBarListenerController(progressBar);
	/**
	 * this logic (useEffect -> setTimeout -> setState) is necessary to fix a
	 * rendering issue where the progress bar animation starts loading at 99%.
	 * this should be investigated more and solved in a future ticket.
	 */
	useEffect(() => {
		const timeout = setTimeout(() => {
			setAttributes(
				getStyledAttributes(
					status,
					duration,
					6000, // ssrProgressBarDuration,
					80, // ssrProgressBarLoadPercent
				),
			);
		}, 0);

		return () => clearTimeout(timeout);
	}, [duration, status]);

	const dynamicLoadingStyles = generateLoadingBarStyles({
		...attributes,
		background: highlightColor,
	});

	return (
		<style
			data-testid={isNav4 ? NAV4_ID : NAV3_ID}
			dangerouslySetInnerHTML={{ __html: dynamicLoadingStyles }}
			data-vc={`global-progress-bar-nav${isNav4 ? '4' : '3'}`}
		/>
	);
};
